import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { validateMyGarageToken } from '@/services/api/mygarage-auth';
import { getOrderComments, submitReview } from '@/services/api/artisan-order-comments';
import { ArtisanOrder } from '@/services/api/artisan-orders';
import { AISHA_API_BASE } from '@/constants';

const OrderReviewView: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<ArtisanOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const init = async () => {
      try {
        let authed = false;
        try {
          const user = await validateMyGarageToken();
          if (user) authed = true;
        } catch (e) { console.error('MyGarage認証エラー:', e); }
        if (!authed) {
          const smsUserId = localStorage.getItem('aineko-sms-user-id');
          if (!smsUserId) {
            setError('ログインが必要です');
            setLoading(false);
            return;
          }
        }
        if (!orderId) return;
        const data = await getOrderComments(parseInt(orderId));
        setOrder(data.order);

        if (data.order.status === 'reviewed') {
          setRating(data.order.review_rating || 0);
          setComment(data.order.review_comment || '');
          setImagePreview(data.order.review_image_url || '');
          setSubmitted(true);
        } else if (data.order.status !== 'shipped') {
          setError('発送済の注文のみ評価できます');
        }
      } catch {
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [orderId]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const uploadImage = async (): Promise<string> => {
    if (!imageFile) return '';
    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('image_type', 'order_review');
    const response = await fetch(`${AISHA_API_BASE}/menu-images/upload/`, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    if (data.public_url) return data.public_url;
    throw new Error(data.error || '画像のアップロードに失敗しました');
  };

  const handleSubmit = async () => {
    if (!orderId || rating === 0) {
      setError('星評価を選択してください');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      let imageUrl = '';
      if (imageFile) {
        imageUrl = await uploadImage();
      }
      await submitReview(parseInt(orderId), {
        review_rating: rating,
        review_comment: comment,
        review_image_url: imageUrl,
      });
      setSubmitted(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-gray-950 text-gray-400 flex items-center justify-center">読み込み中...</div>;

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      <header className="bg-gray-900/80 backdrop-blur-sm border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-xl mx-auto px-4 py-3 flex items-center justify-between">
          <span className="font-bold text-lg">到着評価</span>
          <button onClick={() => navigate('/aineko/orders')} className="text-sm text-gray-400 hover:text-white">
            ← 注文一覧
          </button>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 py-8">
        {error && !order && (
          <div className="text-center text-red-400 py-16">{error}</div>
        )}

        {order && (
          <div className="space-y-6">
            {/* Order info */}
            <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex items-start gap-4">
              {order.image_url && (
                <img src={order.image_url} alt="" className="w-16 h-16 object-cover rounded-lg border border-gray-600" />
              )}
              <div className="text-sm text-gray-300">
                <p className="font-medium text-white">{order.product_name}</p>
                <p>注文 #{order.id}</p>
              </div>
            </div>

            {submitted ? (
              <div className="text-center space-y-4 py-8">
                <div className="text-5xl">🎉</div>
                <h2 className="text-xl font-bold">評価ありがとうございます!</h2>
                <div className="text-yellow-400 text-2xl">
                  {'★'.repeat(rating)}{'☆'.repeat(5 - rating)}
                </div>
                {comment && <p className="text-gray-300 text-sm">{comment}</p>}
                {imagePreview && (
                  <img src={imagePreview} alt="レビュー画像" className="max-h-48 mx-auto rounded-lg" />
                )}
                <button
                  onClick={() => navigate('/aineko/orders')}
                  className="mt-4 bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  注文一覧に戻る
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Star rating */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">商品の評価</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setRating(star)}
                        className="text-4xl"
                      >
                        {star <= rating ? (
                          <span className="text-yellow-400">★</span>
                        ) : (
                          <span className="text-gray-600">☆</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Photo upload */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">写真（任意）</label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-600 rounded-xl p-6 cursor-pointer hover:border-orange-500 transition-colors text-center"
                  >
                    {imagePreview ? (
                      <img src={imagePreview} alt="" className="max-h-48 mx-auto rounded-lg" />
                    ) : (
                      <div className="text-gray-500">
                        <div className="text-3xl mb-2">📷</div>
                        <p className="text-sm">クリックして写真をアップロード</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Comment */}
                <div>
                  <label className="block text-sm text-gray-400 mb-2">コメント（任意）</label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    placeholder="商品の感想を入力..."
                    className="w-full p-3 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm placeholder-gray-500 resize-y"
                  />
                </div>

                {error && <p className="text-red-400 text-sm">{error}</p>}

                <button
                  onClick={handleSubmit}
                  disabled={submitting || rating === 0}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg py-4 rounded-xl shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50"
                >
                  {submitting ? '送信中...' : '評価を投稿する'}
                </button>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default OrderReviewView;
